import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { DataGrid, GridDensity } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { Button, Menu, MenuItem } from '@mui/material';
import { ExportCsv } from '../ExportData/ExportCsv';
import { ExportXlsx } from '../ExportData/ExportXlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from 'react-query';

interface Props {
  data: any;
  columns: any;
  fulltextSearch?: boolean;
  height?: number;
  width?: number;
  pageSize?: number;
  rowsPerPageOptions?: number;
  density?: GridDensity;
  onCreateClick?: any;
  onEditClick?: any;
  onDeleteClick?: any;
  exportOptions?: any;
  autoRowHeight?: boolean;
  readOnly?: boolean;
}

QuickSearchToolbar.propTypes = {
  clearSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export function DataGridTable({
  data,
  columns,
  fulltextSearch = false,
  height = 250,
  width = 1,
  pageSize = 100,
  rowsPerPageOptions = 100,
  density = 'compact',
  onCreateClick,
  onEditClick,
  onDeleteClick,
  exportOptions,
  autoRowHeight,
  readOnly,
}: Props) {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState(data);

  const handleCellEditCommit = useCallback(
    function ({ id, field, value }) {
      if (field === 'qty') {
        const [qty] = value.toString().split(' ');
        const updatedRows = rows.map((row: any) => {
          if (row.id === id) {
            if (row.qty === Number(qty) || qty === '') {
              return row;
            }
            row = { ...row, qty };
            if (row.qty !== '') {
              if (row.isTmp && row.qty !== '0') {
                onCreateClick(row);
              } else {
                if (row.qty === '0') {
                  onDeleteClick(row.id);
                } else {
                  onEditClick(row);
                }
              }
            }
          }
          return row;
        });
        setRows(updatedRows);
        // používá se zatím jen na plánování qty
        // TODO zobecnit
        queryClient.invalidateQueries(['plannedQty']);
      }
    },
    [rows, onCreateClick, onDeleteClick, onEditClick],
  );

  const requestSearch = (searchValue: any) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = data.filter((row: any) => {
      return Object.keys(row).some((field) => {
        if (checkColumns(field, columns)) {
          if (typeof row[field] !== 'object') {
            return searchRegex.test(row[field].toString());
          } else {
            return searchRegex.test(moment(row[field]).format(''));
          }
        } else {
          return null;
        }
      });
    });
    setRows(filteredRows);
  };

  useEffect(() => {
    setRows(data);
  }, [data]);

  return (
    <>
      <Box sx={{ maxHeight: height, width: width }}>
        {!fulltextSearch && (
          <DataGrid
            autoHeight
            disableSelectionOnClick
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            getRowHeight={autoRowHeight ? () => 'auto' : undefined}
            rowsPerPageOptions={[rowsPerPageOptions]}
            density={density}
            onCellEditCommit={readOnly ? undefined : handleCellEditCommit}
            autoPageSize={true}
          />
        )}
        {fulltextSearch && (
          <DataGrid
            autoHeight
            components={{ Toolbar: QuickSearchToolbar }}
            rows={rows}
            columns={columns}
            componentsProps={{
              toolbar: {
                value: searchText,
                onChange: (event: any) => requestSearch(event.target.value),
                clearSearch: () => requestSearch(''),
                data: data,
                exportOptions: exportOptions,
              },
            }}
            pageSize={pageSize}
            getRowHeight={autoRowHeight ? () => 'auto' : undefined}
            rowsPerPageOptions={[rowsPerPageOptions]}
            density={density}
            onCellEditCommit={readOnly ? undefined : handleCellEditCommit}
          />
        )}
      </Box>
    </>
  );
}

function escapeRegExp(value: any) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function checkColumns(field: any, columns: any) {
  return columns.some((item: any) => field === item.field);
}

interface PropsToolbar {
  clearSearch: any;
  onChange: any;
  value: string;
  data: any;
  exportOptions: any;
}

function QuickSearchToolbar({ clearSearch, onChange, value, data, exportOptions }: PropsToolbar) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <Box
      sx={{
        p: 0.5,
        pb: 0,
        justifyContent: 'space-between',
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      {exportOptions && (
        <>
          <Button
            id="demo-positioned-button"
            aria-controls={open ? 'demo-positioned-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            variant={'outlined'}
          >
            <FileDownloadIcon /> Export
          </Button>
          <Menu
            id="demo-positioned-menu"
            aria-labelledby="demo-positioned-button"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem onClick={handleClose}>
              <ExportXlsx
                data={data}
                filterColumns={exportOptions.columns}
                fileName={exportOptions.fileName}
              />
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <ExportCsv
                data={data}
                filterColumns={exportOptions.columns}
                fileName={exportOptions.fileName}
              />
            </MenuItem>
          </Menu>
        </>
      )}
      <div></div>
      <TextField
        variant="standard"
        value={value}
        onChange={onChange}
        placeholder="Search…"
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear"
              size="small"
              style={{ visibility: value ? 'visible' : 'hidden' }}
              onClick={clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
        sx={{
          width: {
            xs: 1,
            sm: 'auto',
          },
          m: (theme) => theme.spacing(1, 0.5, 1.5),
          '& .MuiSvgIcon-root': {
            mr: 0.5,
          },
          '& .MuiInput-underline:before': {
            borderBottom: 1,
            borderColor: 'divider',
          },
        }}
      />
    </Box>
  );
}
